/**
 * Auth state primitives — Clerk-style composable components.
 *
 * <Authenticated> — renders children only when session is valid
 * <Unauthenticated> — renders children when no session
 * <Loading> — renders children during session validation
 *
 * Usage:
 *   <AuthProvider config={...}>
 *     <Loading><Spinner /></Loading>
 *     <Authenticated><App /></Authenticated>
 *     <Unauthenticated><Redirect to="/login" /></Unauthenticated>
 *   </AuthProvider>
 */

import type { ReactNode } from 'react'
import { useAuth } from './store'

interface AuthStateProps {
  children: ReactNode
}

/** English default for the line AuthLoading shows while the sign-in service is unreachable. */
const SESSION_CHECK_UNAVAILABLE_TEXT = 'Cannot reach the sign-in service. Retrying.'

interface AuthLoadingProps extends AuthStateProps {
  /** Replaces SESSION_CHECK_UNAVAILABLE_TEXT, e.g. with a translated line. */
  unavailableText?: string
}

/**
 * Renders children only when auth is loading (session validation in progress).
 * While the session check keeps retrying because the sign-in service is
 * unavailable, it also shows one status line, pinned to the bottom of the
 * viewport so the app's own loading layout is not pushed down.
 */
export function AuthLoading({ children, unavailableText }: AuthLoadingProps) {
  const { isLoading, sessionCheckUnavailable } = useAuth()
  if (!isLoading) return null
  return (
    <>
      {children}
      {sessionCheckUnavailable && (
        <p
          role="status"
          style={{ position: 'fixed', left: 0, right: 0, bottom: '2rem', margin: 0, textAlign: 'center' }}
        >
          {unavailableText ?? SESSION_CHECK_UNAVAILABLE_TEXT}
        </p>
      )}
    </>
  )
}

/** Renders children only when user is authenticated */
export function Authenticated({ children }: AuthStateProps) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading || !isAuthenticated) return null
  return <>{children}</>
}

/** Renders children only when user is NOT authenticated (and not loading) */
export function Unauthenticated({ children }: AuthStateProps) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading || isAuthenticated) return null
  return <>{children}</>
}
